import {
  compareSections,
  type DocumentSection,
  type RenderedDocument,
  type RenderedSection,
} from '@oscom/shared';

export type MoveAction = 'up' | 'down' | 'indent' | 'outdent';

export interface MoveTarget {
  parentSectionId?: string;
  order: number;
}

function siblingsOf(
  sections: readonly DocumentSection[],
  parentId: string | undefined,
): DocumentSection[] {
  return sections.filter((section) => section.parentSectionId === parentId).sort(compareSections);
}

function target(order: number, parentId: string | undefined): MoveTarget {
  return parentId ? { parentSectionId: parentId, order } : { order };
}

/** Order for a new last child of `parentId` (top level when undefined). */
export function nextChildOrder(
  sections: readonly DocumentSection[],
  parentId: string | undefined,
): number {
  const siblings = siblingsOf(sections, parentId);
  return siblings.length > 0 ? Math.max(...siblings.map((section) => section.order)) + 1 : 1;
}

/**
 * Where a tree action puts a section: one write with a new parent and/or
 * order. Up and down use the midpoint between the neighbours, so no sibling
 * has to be renumbered. `undefined` when the action is not possible.
 */
export function planMove(
  sections: readonly DocumentSection[],
  sectionId: string,
  action: MoveAction,
): MoveTarget | undefined {
  const section = sections.find((entry) => entry.id === sectionId);
  if (!section) return undefined;
  const parentId = section.parentSectionId;
  const siblings = siblingsOf(sections, parentId);
  const index = siblings.findIndex((entry) => entry.id === sectionId);
  switch (action) {
    case 'up': {
      const previous = siblings[index - 1];
      if (!previous) return undefined;
      const before = siblings[index - 2];
      return target(before ? (before.order + previous.order) / 2 : previous.order - 1, parentId);
    }
    case 'down': {
      const next = siblings[index + 1];
      if (!next) return undefined;
      const after = siblings[index + 2];
      return target(after ? (next.order + after.order) / 2 : next.order + 1, parentId);
    }
    case 'indent': {
      const previous = siblings[index - 1];
      if (!previous) return undefined;
      return target(nextChildOrder(sections, previous.id), previous.id);
    }
    case 'outdent': {
      if (!parentId) return undefined;
      const parent = sections.find((entry) => entry.id === parentId);
      if (!parent) return undefined;
      const parentSiblings = siblingsOf(sections, parent.parentSectionId);
      const next = parentSiblings[parentSiblings.findIndex((entry) => entry.id === parent.id) + 1];
      return target(
        next ? (parent.order + next.order) / 2 : parent.order + 1,
        parent.parentSectionId,
      );
    }
  }
}

function walk(
  sections: readonly RenderedSection[],
  visit: (section: RenderedSection) => void,
): void {
  for (const section of sections) {
    visit(section);
    walk(section.children, visit);
  }
}

/** Stored section id -> number from the render result (the single numbering). */
export function sectionNumbers(rendered: RenderedDocument | undefined): Map<string, string> {
  const numbers = new Map<string, string>();
  if (rendered) {
    walk(rendered.sections, (section) => {
      if (section.sectionId) numbers.set(section.sectionId, section.number);
    });
  }
  return numbers;
}

export function findRenderedSection(
  rendered: RenderedDocument | undefined,
  sectionId: string,
): RenderedSection | undefined {
  let found: RenderedSection | undefined;
  if (rendered) {
    walk(rendered.sections, (section) => {
      if (!found && section.sectionId === sectionId) found = section;
    });
  }
  return found;
}
